import {
  apiCollectionToRowResponse,
  rowQueryToApiParams
} from "../modules/tableQuery";

import { AdminContainer } from "../components/layouts";
import MaterialTable, { MTableToolbar } from "material-table";
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import api from "../api";
import PaymentMethodSelector from "../components/PaymentMethodSelector";
import StatusChangeDialog from "../components/StatusChangeDialog";
import CustomMUIToolbar from "../components/CustomMUIToolbar";
import { formatMoney } from "../lithic/Money";
import { renderTableLink } from "../components/renderTableCell";
import tableIcons from "../components/tableIcons";
import { useTheme } from "@material-ui/core/styles";

import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Button
} from "@material-ui/core";
import { Search as SearchIcon, Clear as ClearIcon } from "@material-ui/icons";
// lodash removed as unused
import dayjs from "dayjs";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import Link from "../components/Link";
import qs from "../lithic/queryString";

// Custom filter component for status
const StatusFilter = ({ columnDef, onFilterChanged }) => {
  const [selected, setSelected] = useState("");

  const handleChange = (event) => {
    const value = event.target.value;
    setSelected(value);

    // Pass empty array to clear filter, or array with single value
    onFilterChanged(columnDef.tableData.id, value === "" ? [] : [value]);
  };

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <Select value={selected} onChange={handleChange} displayEmpty>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="finished">Paid</MenuItem>
        <MenuItem value="pending">Unpaid</MenuItem>
      </Select>
    </FormControl>
  );
};

// Custom filter component for payType
const PayTypeFilter = ({ columnDef, onFilterChanged }) => {
  const [selected, setSelected] = useState("");

  const handleChange = (event) => {
    const value = event.target.value;
    setSelected(value);

    // Pass empty array to clear filter, or array with single value
    onFilterChanged(columnDef.tableData.id, value === "" ? [] : [value]);
  };

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <Select value={selected} onChange={handleChange} displayEmpty>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="pay_in">Invoice</MenuItem>
        <MenuItem value="pay_out">Payout</MenuItem>
      </Select>
    </FormControl>
  );
};

// Custom filter component for billing period
const BillingPeriodFilter = ({ columnDef, onFilterChanged }) => {
  const [selected, setSelected] = useState("");

  const handleChange = (event) => {
    const value = event.target.value;
    setSelected(value);

    // Pass empty array to clear filter, or array with single value
    onFilterChanged(columnDef.tableData.id, value === "" ? [] : [value]);
  };

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <Select value={selected} onChange={handleChange} displayEmpty>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="monthly">Monthly</MenuItem>
        <MenuItem value="weekly">Weekly</MenuItem>
      </Select>
    </FormControl>
  );
};

// Custom filter component for subject type (partner vs property)
const SubjectTypeFilter = ({ columnDef, onFilterChanged }) => {
  const [selected, setSelected] = useState("partner");

  const handleChange = (event) => {
    const value = event.target.value;
    setSelected(value);

    onFilterChanged(columnDef.tableData.id, value === "" ? [] : [value]);
  };

  // Fire the default "partner" filter on mount
  React.useEffect(() => {
    onFilterChanged(columnDef.tableData.id, ["partner"]);
  }, []);

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <Select value={selected} onChange={handleChange} displayEmpty>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="partner">Partner</MenuItem>
        <MenuItem value="property">Property</MenuItem>
      </Select>
    </FormControl>
  );
};

// Custom filter component for month
const MonthFilter = ({ columnDef, onFilterChanged }) => {
  const [selected, setSelected] = useState("");

  const handleChange = (event) => {
    const value = event.target.value;
    setSelected(value);

    // Pass empty array to clear filter, or array with single value
    onFilterChanged(columnDef.tableData.id, value === "" ? [] : [value]);
  };

  // Generate month options (January - December)
  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <Select value={selected} onChange={handleChange} displayEmpty>
        <MenuItem value="">All Months</MenuItem>
        {months.map((month) => (
          <MenuItem key={month.value} value={month.value}>
            {month.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Custom text filter component for Partner column
const PartnerFilter = ({ initValue, columnDef, onFilterChanged }) => {
  const [value, setValue] = useState(initValue || "");
  const [debouncedValue, setDebouncedValue] = useState("");

  // Debounce filter input to avoid excessive API calls
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedValue(value);
    }, 500); // 500ms delay

    return () => clearTimeout(timerId);
  }, [value]);

  // Apply filter when debounced value changes
  useEffect(() => {
    // Only filter if there's text entered
    if (debouncedValue.trim() !== "")
      onFilterChanged(columnDef.tableData.id, [debouncedValue]);
    else onFilterChanged(columnDef.tableData.id, []);
  }, [debouncedValue, columnDef.tableData.id, onFilterChanged]);

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  const clearFilter = () => {
    setValue("");
    setDebouncedValue("");
  };

  return (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      placeholder="Search partners..."
      value={value}
      onChange={handleChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={clearFilter}
              aria-label="clear filter"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null
      }}
    />
  );
};

export default function StatementsListPage() {
  const tableRef = useRef();
  const theme = useTheme();
  const location = useLocation();
  const partnerId = qs.parse(location.search)["partnerId"];
  const [paymentMethodSelectorOpen, setPaymentMethodSelectorOpen] =
    useState(false);
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [editingStatementId, setEditingStatementId] = useState(null);

  const columns = React.useMemo(() => {
    return makeColumns(
      theme,
      setEditingStatementId,
      setPaymentMethodSelectorOpen,
      setStatusChangeDialogOpen
    );
  }, [
    theme,
    setEditingStatementId,
    setPaymentMethodSelectorOpen,
    setStatusChangeDialogOpen
  ]);

  return (
    <AdminContainer variant="list">
      <MaterialTable
        title="Statements"
        tableRef={tableRef}
        columns={columns}
        options={{
          selection: true,
          selectionProps: (rowData) => ({
            disabled: rowData.status === "finished",
            color: "primary"
          }),
          showSelectAllCheckbox: false,
          emptyRowsWhenPaging: false,
          pageSize: 50,
          pageSizeOptions: [50, 100, 200],
          sorting: true,
          thirdSortClick: false,
          filtering: true,
          exportButton: true
        }}
        data={(q) => {
          const params = rowQueryToApiParams(q);
          // Debug logs removed

          return api
            .listStatements({
              ...params,
              owner_id: Number(partnerId) || undefined
            })
            .then((resp) => {
              return apiCollectionToRowResponse(resp);
            });
        }}
        icons={tableIcons}
        components={{
          Toolbar: (props) => (
            <>
              {props.selectedRows.length ? (
                <CustomMUIToolbar tableRef={tableRef} muiProps={props} />
              ) : (
                <MTableToolbar {...props} showTextRowsSelected={false} />
              )}
            </>
          ),
          FilterRow: (props) => {
            return (
              <tr>
                <td></td>
                {props.columns.map((columnDef) => (
                  <td key={columnDef.tableData.id}>
                    {columnDef.field === "status" && (
                      <StatusFilter
                        columnDef={columnDef}
                        onFilterChanged={props.onFilterChanged}
                      />
                    )}
                    {columnDef.field === "payType" && (
                      <PayTypeFilter
                        columnDef={columnDef}
                        onFilterChanged={props.onFilterChanged}
                      />
                    )}
                    {columnDef.field === "billingPeriod" && (
                      <BillingPeriodFilter
                        columnDef={columnDef}
                        onFilterChanged={props.onFilterChanged}
                      />
                    )}
                    {columnDef.field === "periodStart" && (
                      <MonthFilter
                        columnDef={columnDef}
                        onFilterChanged={props.onFilterChanged}
                      />
                    )}
                    {columnDef.field === "partnerName" && (
                      <PartnerFilter
                        columnDef={columnDef}
                        onFilterChanged={props.onFilterChanged}
                      />
                    )}
                    {columnDef.field === "subjectType" && (
                      <SubjectTypeFilter
                        columnDef={columnDef}
                        onFilterChanged={props.onFilterChanged}
                      />
                    )}
                  </td>
                ))}
              </tr>
            );
          }
        }}
      />

      {/* Payment Method Selector Modal */}
      {paymentMethodSelectorOpen && editingStatementId && (
        <PaymentMethodSelector
          open={paymentMethodSelectorOpen}
          statementId={editingStatementId}
          onClose={() => {
            setPaymentMethodSelectorOpen(false);
            setEditingStatementId(null);
          }}
          onSave={(updatedStatement) => {
            setPaymentMethodSelectorOpen(false);
            setEditingStatementId(null);
            // Refresh the data
            if (tableRef.current) tableRef.current.onQueryChange();
          }}
        />
      )}

      {/* Status Change Dialog */}
      {statusChangeDialogOpen && editingStatementId && (
        <StatusChangeDialog
          open={statusChangeDialogOpen}
          statementId={editingStatementId}
          onClose={() => {
            setStatusChangeDialogOpen(false);
            setEditingStatementId(null);
          }}
          onSave={(updatedStatement) => {
            setStatusChangeDialogOpen(false);
            setEditingStatementId(null);
            // Refresh the data
            if (tableRef.current) tableRef.current.onQueryChange();
          }}
        />
      )}
    </AdminContainer>
  );
}

function makeColumns(
  theme,
  handleEditStatementId,
  handleSetPaymentMethodSelectorOpen,
  handleSetStatusChangeDialogOpen
) {
  const statementDetailRenderTableLink = (rowData, body) =>
    renderTableLink({
      url: `partners/${rowData.partnerId}/statement/`,
      body
    })(rowData);

  // partnerDetailRenderTableLink was unused and removed

  const paymentRenderTableLink = (rowData, url, body) => {
    if (!url) return body;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", alignItems: "center" }}
      >
        {body} <OpenInNewIcon fontSize="small" style={{ marginLeft: 4 }} />
      </a>
    );
  };

  let columns = [
    {
      title: "Id",
      field: "id",
      defaultSort: "desc",
      width: 20,
      filtering: false,
      render: (rowData) => (
        <Link
          to={`statements/${rowData.id}`}
          style={{ color: theme.palette.dark.main }}
        >
          {rowData.id}
        </Link>
      )
    },
    {
      title: "Partner",
      field: "partnerName",
      sorting: false,
      filtering: true,
      render: (rowData) => {
        if (rowData.partnerName && rowData.partnerId)
          return (
            <Link
              to={`partners/${rowData.partnerId}`}
              style={{ color: theme.palette.dark.main }}
            >
              {rowData.partnerName}
            </Link>
          );
        return rowData.partnerName || "-";
      }
    },
    {
      title: "Property",
      field: "propertyName",
      sorting: false,
      filtering: false,
      render: (rowData) => rowData.propertyName || "-"
    },
    {
      title: "Subject",
      field: "subjectType",
      sorting: false,
      filtering: true,
      render: (rowData) => (rowData.propertyName ? "Property" : "Partner")
    },
    {
      title: "Period",
      field: "billingPeriod",
      sorting: false,
      filtering: true,
      render: (rowData) => {
        return rowData.billingPeriod === "weekly" ? "Weekly" : "Monthly";
      }
    },
    {
      title: "Month",
      field: "periodStart",
      sorting: false,
      filtering: true,
      render: (rowData) =>
        statementDetailRenderTableLink(rowData, (rowData) => {
          if (rowData.period) {
            if (rowData.billingPeriod === "weekly")
              return "Week of " + dayjs.utc(rowData.period.start).format("MMM D, YYYY");
            return dayjs.utc(rowData.period.start).format("MMMM YYYY");
          }
          return "-";
        })
    },
    {
      title: "Status",
      field: "status",
      sorting: false,
      filtering: true,
      render: (rowData) => {
        const statusText = rowData.status === "finished" ? "Paid" : "Unpaid";

        // Display action button only for unpaid statements
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%"
            }}
          >
            <span>{statusText}</span>
            {rowData.status === "pending" && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                style={{
                  marginLeft: 4,
                  padding: "0px 8px",
                  minWidth: "auto",
                  fontSize: "0.7rem",
                  height: "24px"
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditStatementId(rowData.id);
                  handleSetStatusChangeDialogOpen(true);
                }}
              >
                Mark Paid
              </Button>
            )}
          </div>
        );
      }
    },
    {
      title: "Type",
      field: "payType",
      sorting: false,
      filtering: true,
      render: (rowData) =>
        statementDetailRenderTableLink(rowData, (rowData) => {
          return rowData.payType === "pay_in" ? "Invoice" : "Payout";
        })
    },
    {
      title: "Payment ID",
      field: "paymentId",
      sorting: false,
      filtering: false,
      render: (rowData) => {
        // Use paymentId from API
        const paymentId = rowData.paymentId || "-";
        const paymentType = rowData.payType;
        const url =
          paymentId !== "-"
            ? paymentType === "pay_in"
              ? `/invoices/${paymentId}`
              : `/payouts/${paymentId}`
            : null;

        return paymentRenderTableLink(rowData, url, paymentId);
      }
    },
    {
      title: "Payment Method",
      field: "paymentMethod",
      sorting: false,
      filtering: false,
      width: "180px", // Fixed width
      render: (rowData) => {
        if (rowData.bankAccount) {
          const accountName = rowData.bankAccount.name || "";
          const last4 = rowData.bankAccount.last4
            ? `****${rowData.bankAccount.last4}`
            : "";
          // Compact display format
          const displayText = last4 ? `${last4}` : accountName;

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                overflow: "hidden"
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "120px" // Ensure text doesn't overflow
                }}
                title={`${accountName} ${last4}`.trim()}
              >
                {displayText}
              </span>
              {rowData.paymentId && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  style={{
                    marginLeft: 4,
                    padding: "0px 4px",
                    minWidth: "auto",
                    fontSize: "0.7rem",
                    height: "20px"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditStatementId(rowData.id);
                    handleSetPaymentMethodSelectorOpen(true);
                  }}
                >
                  Edit
                </Button>
              )}
            </div>
          );
        }
        return "-";
      }
    },
    {
      title: "Balance",
      field: "balance",
      sorting: false,
      filtering: false,
      render: (rowData) =>
        statementDetailRenderTableLink(rowData, (rowData) => {
          return formatMoney(rowData.balance);
        })
    },
    {
      title: "ACH Status",
      field: "achStatus",
      sorting: false,
      filtering: false,
      render: (rowData) => {
        if (rowData.achLinks && rowData.achLinks.length > 0)
          return (
            <>
              {rowData.achLinks.map((link, index) => (
                <div key={index}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {link.status ||
                      (link.name.includes("Transaction")
                        ? "ACH Transaction"
                        : "ACH Transfer")}
                  </a>
                </div>
              ))}
            </>
          );

        return "-";
      }
    }
  ];

  return columns;
}
